

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAllTaskTracking,
  updateTaskTrackingRemarks,
} from "../redux/actions/taskTrackingActions";
import { fetchDesignations } from "../redux/actions/designationActions";
import {
  FiSearch,
  FiDownload,
  FiEdit3,
  FiCheck,
  FiFilter,
  FiX,
  FiClock,
  FiCalendar,
  FiUser,
  FiPhone,
  FiBriefcase,
  FiMapPin,
  FiChevronLeft,
  FiChevronRight,
  FiNavigation
} from "react-icons/fi";
import Papa from "papaparse";

const formatIndianDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// const formatTime = (timeStr) => {
//   if (!timeStr) return "-";
//   return new Date(timeStr).toLocaleTimeString("en-IN", {
//     hour: '2-digit',
//     minute: '2-digit'
//   });
// };
const formatTime = (timeStr, dateStr) => {
  if (!timeStr || !dateStr) return "-";

  const date = new Date(dateStr);
  const [hh, mm, ss] = timeStr.split(":");

  date.setHours(hh);
  date.setMinutes(mm);
  date.setSeconds(ss);

  return date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit"
  });
};


const TaskTracking = () => {
  const dispatch = useDispatch();
  const { taskTrackingData, loading, error, meta = {} } = useSelector(
    (state) => state.taskTracking
  );

  const { designations = [] } = useSelector(
    (state) => state.designation || {}
  );

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [selectedDesignation, setSelectedDesignation] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [updatedRemark, setUpdatedRemark] = useState("");
  const [statusDropdown, setStatusDropdown] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [mapAddress, setMapAddress] = useState("");
  const [mapLoading, setMapLoading] = useState(false);
  const [currentLocation, setCurrentLocation] = useState({ type: "", lat: null, lng: null });

  const totalPages = meta?.totalPages || 1;

  const API_KEY = process.env.REACT_APP_MAP_API_KEY;

  useEffect(() => {
    fetchData();
    dispatch(fetchDesignations());
  }, [
    dispatch,
    currentPage,
    selectedDesignation,
    searchTerm,
    fromDate,
    toDate,
    statusDropdown,
  ]);

  const fetchData = () => {
    const query = {
      page: currentPage,
      limit: itemsPerPage,
      search: searchTerm,
      fromDate,
      toDate,
      status: statusDropdown,
      designation: selectedDesignation,
    };
    dispatch(fetchAllTaskTracking(query));
  };

  const fetchAddress = async (lat, lng) => {
    try {
      setMapLoading(true);
      const res = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${API_KEY}`
      );
      const data = await res.json();
      setMapAddress(
        data.status === "OK"
          ? data.results[0]?.formatted_address || "Address not found"
          : "Unable to fetch address"
      );
    } catch (e) {
      setMapAddress("Error fetching address");
    } finally {
      setMapLoading(false);
    }
  };

  const handleViewMap = async (lat, lng, type = "") => {
    if (!lat || !lng) return;
    setMapModalOpen(true);
    setCurrentLocation({ type, lat, lng });
    await fetchAddress(lat, lng);
  };

  const closeMapModal = () => {
    setMapModalOpen(false);
    setMapAddress("");
    setCurrentLocation({ type: "", lat: null, lng: null });
  };

  const resetFilters = () => {
    setSelectedDesignation("");
    setSearchTerm("");
    setFromDate("");
    setToDate("");
    setStatusDropdown("");
    setCurrentPage(1);
  };

  const handleExportCSV = () => {
    const exportData = taskTrackingData.map((item) => ({
      Task: item.task_name || "-",
      User: item.user_name || "-",
      Phone: item.user_phone || "-",
      Designation: item.designation || "-",
      "Check In": formatTime(item.start_time),
      "Check Out": formatTime(item.end_time),
      "Start Location": item.start_latitude && item.start_longitude ? "Available" : "Not Available",
      "End Location": item.end_latitude && item.end_longitude ? "Available" : "Not Available",
      Date: formatIndianDate(item.created_at),
      Remarks: item.task_tracking_remarks || "-",
      Status: item.status || "-",
    }));

    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `Task_Tracking_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSaveRemarks = (id) => {
    if (!updatedRemark.trim()) return;
    dispatch(updateTaskTrackingRemarks(id, updatedRemark));
    setEditingId(null);
    setUpdatedRemark("");
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      ongoing: { 
        color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300", 
        label: "Ongoing" 
      },
      complete: { 
        color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300", 
        label: "Complete" 
      },
    };

    const config = statusConfig[status] || { 
      color: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300", 
      label: status 
    };
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color} capitalize`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400 text-lg">Loading Task Tracking...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Task Tracking
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Monitor and manage task progress in real-time
          </p>
        </div>
        <div className="flex items-center gap-3 mt-4 lg:mt-0">
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-xl transition-colors"
          >
            <FiDownload className="text-lg" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Search Input */}
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
            <input
              type="text"
              placeholder="Search by name, phone, or task..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-600 transition text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            />
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 px-4 py-3 rounded-xl transition-colors"
          >
            <FiFilter className="text-lg" />
            Filters
            {(selectedDesignation || statusDropdown || fromDate || toDate) && (
              <span className="bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {[selectedDesignation, statusDropdown, fromDate, toDate].filter(Boolean).length}
              </span>
            )}
          </button>

          {/* Clear Filters */}
          {(selectedDesignation || statusDropdown || fromDate || toDate || searchTerm) && (
            <button
              onClick={resetFilters}
              className="flex items-center gap-2 text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 px-4 py-3 rounded-xl transition-colors"
            >
              <FiX className="text-lg" />
              Clear
            </button>
          )}
        </div>

        {/* Expanded Filters */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Designation
              </label>
              <select
                value={selectedDesignation}
                onChange={(e) => {
                  setSelectedDesignation(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full bg-gray-50 dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition"
              >
                <option value="">All Designations</option>
                {designations.map((d, i) => (
                  <option key={i} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Status
              </label>
              <select
                value={statusDropdown}
                onChange={(e) => {
                  setStatusDropdown(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full bg-gray-50 dark:bg-gray-700 border-0 rounded-xl py-3 px-4 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition"
              >
                <option value="">All Status</option>
                <option value="ongoing">Ongoing</option>
                <option value="complete">Complete</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                From Date
              </label>
              <div className="relative">
                <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => {
                    setFromDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-600 transition text-gray-900 dark:text-white"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                To Date
              </label>
              <div className="relative">
                <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => {
                    setToDate(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 dark:bg-gray-700 border-0 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-600 transition text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-gray-600 dark:text-gray-400">
          Showing {taskTrackingData.length} of {meta?.total || 0} tracking records
        </p>
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <span>Page {currentPage} of {totalPages}</span>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  #
                </th>
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Task Details
                </th>
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <FiUser />
                    Employee
                  </div>
                </th>
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <FiClock />
                    Time Tracking
                  </div>
                </th>
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <FiMapPin />
                    Locations
                  </div>
                </th>
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Date
                </th>
                <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                {/* <th className="py-4 px-6 text-left text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Remarks
                </th> */}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {error ? (
                <tr>
                  <td colSpan={8} className="py-8 px-6 text-center">
                    <div className="text-red-500 dark:text-red-400">
                      {error}
                    </div>
                  </td>
                </tr>
              ) : taskTrackingData.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 px-6 text-center">
                    <FiNavigation className="text-4xl text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400 text-lg mb-2">No tracking records found</p>
                    <p className="text-gray-400 dark:text-gray-500 text-sm">
                      Try adjusting your search or filters
                    </p>
                  </td>
                </tr>
              ) : (
                taskTrackingData.map((item, index) => (
                  <tr key={item.id || index} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    {/* Serial Number */}
                    <td className="py-4 px-6 text-gray-600 dark:text-gray-400 text-sm">
                      {(currentPage - 1) * itemsPerPage + index + 1}
                    </td>

                    {/* Task Details */}
                    <td className="py-4 px-6">
                      <p className="font-medium text-gray-900 dark:text-white">
                        {item.task_name || "-"}
                      </p>
                    </td>

                    {/* Employee Info */}
                    <td className="py-4 px-6">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          {item.user_name || "-"}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <FiPhone className="text-xs" />
                          {item.user_phone || "-"}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                          <FiBriefcase className="text-xs" />
                          {item.designation || "-"}
                        </p>
                      </div>
                    </td>

                    {/* Time Tracking */}
                    <td className="py-4 px-6">
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Check In</p>
                          <p className="text-sm text-gray-900 dark:text-white font-medium">
                            {/* {formatTime(item.start_time)} */}
                            {formatTime(item.start_time, item.created_at)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 dark:text-gray-400">Check Out</p>
                          <p className="text-sm text-gray-900 dark:text-white font-medium">
                            {/* {formatTime(item.end_time) || "-"} */}
                            {formatTime(item.end_time, item.created_at)}

                          </p>
                        </div>
                      </div>
                    </td>

                    {/* Locations */}
                    <td className="py-4 px-6">
                      <div className="space-y-2">
                        {item.start_latitude && item.start_longitude && (
                          <button
                            onClick={() => handleViewMap(item.start_latitude, item.start_longitude, "Start")}
                            className="flex items-center gap-1 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 text-xs"
                          >
                            <FiMapPin className="text-xs" />
                            Start Location
                          </button>
                        )}
                        {item.end_latitude && item.end_longitude && (
                          <button
                            onClick={() => handleViewMap(item.end_latitude, item.end_longitude, "End")}
                            className="flex items-center gap-1 text-green-500 hover:text-green-600 dark:text-green-400 dark:hover:text-green-300 text-xs"
                          >
                            <FiMapPin className="text-xs" />
                            End Location
                          </button>
                        )}
                        {!item.start_latitude && !item.end_latitude && (
                          <span className="text-gray-400 text-xs">No locations</span>
                        )}
                      </div>
                    </td>

                    {/* Date */}
                    <td className="py-4 px-6 text-sm text-gray-600 dark:text-gray-400">
                      {formatIndianDate(item.created_at)}
                    </td>

                    {/* Status */}
                    <td className="py-4 px-6">
                      {getStatusBadge(item.status)}
                    </td>

                    {/* Remarks */}
                    {/* <td className="py-4 px-6">
                      {editingId === item.id ? (
                        <div className="flex items-center gap-2">
                          <select
                            value={updatedRemark}
                            onChange={(e) => setUpdatedRemark(e.target.value)}
                            className="w-full bg-gray-50 dark:bg-gray-700 border-0 rounded-lg py-1 px-2 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition"
                          >
                            <option value="">Select Remark</option>
                            <option value="inside">Inside</option>
                            <option value="outside">Outside</option>
                            <option value="completed">Completed</option>
                            <option value="in_progress">In Progress</option>
                          </select>
                          <button
                            onClick={() => handleSaveRemarks(item.id)}
                            className="text-green-500 hover:text-green-600 dark:text-green-400 dark:hover:text-green-300 p-1"
                          >
                            <FiCheck className="text-sm" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-sm ${
                            item.task_tracking_remarks 
                              ? "text-gray-700 dark:text-gray-300" 
                              : "text-gray-400 dark:text-gray-500"
                          }`}>
                            {item.task_tracking_remarks || "No remarks"}
                          </span>
                          <button
                            onClick={() => {
                              setEditingId(item.id);
                              setUpdatedRemark(item.task_tracking_remarks || "");
                            }}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 transition-colors"
                          >
                            <FiEdit3 className="text-sm" />
                          </button>
                        </div>
                      )}
                    </td> */}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="flex items-center gap-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiChevronLeft />
            Previous
          </button>

          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <span>Page {currentPage} of {totalPages}</span>
          </div>

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
            className="flex items-center gap-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <FiChevronRight />
          </button>
        </div>
      )}

      {/* Map Modal */}
      {mapModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {currentLocation.type} Location Address
              </h3>
              <button
                onClick={closeMapModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <FiX className="text-xl" />
              </button>
            </div>
            <div className="p-4">
              {mapLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="ml-2 text-gray-600 dark:text-gray-400">Fetching address...</span>
                </div>
              ) : (
                <div>
                  <p className="text-gray-700 dark:text-gray-300 mb-4">{mapAddress}</p>
                  {currentLocation.lat && currentLocation.lng && (
                    <a
                      href={`https://www.google.com/maps?q=${currentLocation.lat},${currentLocation.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                    >
                      <FiMapPin className="text-sm" />
                      Open in Google Maps
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskTracking;